import { Button, ColorPicker, DatePicker, Flex, Form, Input, Space, Switch } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { Loader } from "~Common/Components";
import { FormInput } from "~components/Form/FormInput";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { UniformItemBottomTypes } from "~enums/uniformItemBottomTypes";
import type { UniformItemColors } from "~enums/uniformItemColors";
import { UniformItemTypes } from "~enums/uniformItemTypes";
import type { UniformTypes } from "~enums/uniformTypes";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { getColorPickerValue } from "~utilities/getColorPickerValue";
import PermissionCheck from "../../Common/PermissionCheck";
import { UniformSet } from "./components/UniformSet";
import { useDeleteUniform } from "./hooks/useDeleteUniform";
import { useDeleteUniformItem } from "./hooks/useDeleteUniformItem";
import useFetchUniformItems from "./hooks/useFetchUniformItems";
import { useUpsertUniform } from "./hooks/useUpsertUniform";
import type { FormType } from "./models/formModel";
import type { IUniformModel } from "./models/IUniformModel";
import { transformUniformItemsResponse } from "./utilities/transformUniformItemsResponse";

const StyledContainer = styled.div`
  background: #ffffff;
  opacity: 1;
  padding: 10px;
  min-width: 700px;

  gap: 15px;
  display: flex;
  flex-direction: row;
  align-items: start;

  & > :first-child {
    flex-grow: 1;
  }

  > svg {
    display: flex;
    flex: 1;
  }
`;
StyledContainer.displayName = "StyledContainer";

const StyledSwitch = styled(Switch)`
  margin: 5px auto 10px auto;
  display: block;
`;
StyledSwitch.displayName = "StyledSwitch";

export interface IUniformProperties {
  baseType: UniformTypes;
  baseId: string;
  uniform?: IUniformModel;
  onAdded?: (uniformId: string) => void;
  onDeleted?: () => void;
}

export const Uniform = (properties: IUniformProperties) => {
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
  const isEdit = !isEmpty(properties.uniform);
  const { uniforms } = useSportConfiguration();
  const {
    data: uniformItemsData,
    isPending: isUniformItemsLoading,
    refetch: refetchUniformItems,
  } = useFetchUniformItems(properties.uniform?.uniformId);
  const uniformItems = uniformItemsData?.uniformItems;
  const { mutateAsync: upsertUniform, isPending: isUpsertUniformInProgress } = useUpsertUniform();
  const { mutateAsync: deleteUniform, isPending: isDeletingUniformInProgress } = useDeleteUniform();
  const { mutateAsync: deleteUniformItem, isPending: isDeletingUniformItemInProgress } = useDeleteUniformItem();

  const [bottomType, setBottomType] = useState<UniformItemBottomTypes>(uniforms.bottomOptions[0] ?? UniformItemBottomTypes.Shorts);

  const initialFormValues = {
    nameLocal: properties.uniform?.nameLocal,
    nameLatin: properties.uniform?.nameLatin,
    dateFrom: isEmpty(properties.uniform?.dateFrom?.split("T")?.[0])
      ? null
      : dayjs(properties.uniform?.dateFrom?.split("T")?.[0], "YYYY-MM-DD"),
    dateTo: isEmpty(properties.uniform?.dateTo?.split("T")?.[0]) ? null : dayjs(properties.uniform?.dateTo?.split("T")?.[0], "YYYY-MM-DD"),
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: provided deps are enough
  const onSubmitButtonHandler = useCallback(async () => {
    const values = await form.validateFields();

    if (values.dateFrom) {
      values.dateFrom = values.dateFrom.format("YYYY-MM-DD");
    }
    if (values.dateTo) {
      values.dateTo = values.dateTo.format("YYYY-MM-DD");
    }

    const items = Object.entries((values as FormType).uniformParts)
      .filter(([, uniformPart]) => Object.values(uniformPart.colors).some(Boolean))
      .map((entry) => {
        const itemType = entry[0] as UniformItemTypes;
        const uniformPart = entry[1];
        return {
          itemType,
          nameLatin: uniformPart.nameLatin,
          nameLocal: uniformPart.nameLocal,
          uniformItemId: uniformPart.uniformItemId,
          colors: Object.entries(uniformPart.colors).reduce(
            (acc, entry) => {
              const colorType = entry[0] as UniformItemColors;
              const color = entry[1];
              acc[colorType] = getColorPickerValue(color);
              return acc;
            },
            {} as { [key in UniformItemColors]?: string },
          ),
        };
      });

    const uniformResponse = await upsertUniform({
      uniformId: properties.uniform?.uniformId,
      upsertData: {
        baseType: properties.baseType,
        baseId: properties.baseId,
        dateFrom: values.dateFrom,
        dateTo: values.dateTo,
        nameLocal: values.nameLocal,
        nameLatin: values.nameLatin,
      },
      items,
    });

    const uniformId = uniformResponse?.data?.[0]?.uniformId;
    if (uniformId && properties.onAdded && !isEdit) {
      properties.onAdded(uniformId);
    }

    refetchUniformItems();
  }, [form, uniformItems, properties.baseType, properties.baseId, properties.onAdded, refetchUniformItems]);

  const onDeleteButtonHandler = useCallback(async () => {
    if (properties.uniform) {
      const deleteItemPromises = (uniformItems ?? []).map((item) => deleteUniformItem(item.uniformItemId));
      await Promise.all(deleteItemPromises);
      await deleteUniform(properties.uniform);
    }
    properties.onDeleted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteUniform, deleteUniformItem, uniformItems, properties.uniform, properties.onDeleted]);

  useEffect(() => {
    if (!uniformItems) return;
    form.setFieldsValue({ uniformParts: uniformItemsData.transformedParts });
  }, [form, uniformItems, uniformItemsData]);

  const uniformParts = Form.useWatch<FormType["uniformParts"]>(["uniformParts"], form);

  const isMutationDisabled =
    isUniformItemsLoading || isUpsertUniformInProgress || isDeletingUniformInProgress || isDeletingUniformItemInProgress;

  if (isUniformItemsLoading) {
    return <Loader />;
  }

  return (
    <StyledContainer>
      <div>
        {uniforms.bottomOptions.length > 1 && (
          <StyledSwitch
            checkedChildren={formatMessage({ id: "uniform.skirts" })}
            unCheckedChildren={formatMessage({ id: "uniform.shorts" })}
            value={bottomType === UniformItemBottomTypes.Skirts}
            onChange={(value) => setBottomType(value ? UniformItemBottomTypes.Skirts : UniformItemBottomTypes.Shorts)}
          />
        )}
        <Form
          form={form}
          key={properties.uniform?.uniformId}
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 12 }}
          initialValues={initialFormValues}
          style={{ marginTop: 10 }}
        >
          <Form.Item
            name={"nameLocal"}
            label={formatMessage({ id: "nameLocal" })}
            tooltip={formatMessage({ id: "form.help.local.name.item" }, { item: formatMessage({ id: "uniform" }) })}
            style={formItemSharedStyles}
            rules={[{ max: 150, required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={"nameLatin"}
            label={formatMessage({ id: "nameLatin" })}
            tooltip={formatMessage({ id: "form.help.latin.name.item" }, { item: formatMessage({ id: "uniform" }) })}
            style={formItemSharedStyles}
            rules={[{ max: 150 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={"dateFrom"}
            label={formatMessage({ id: "date.start" })}
            tooltip={formatMessage({ id: "form.help.start.date.item" }, { item: formatMessage({ id: "uniform" }) })}
            style={formItemSharedStyles}
          >
            <DatePicker size={"middle"} format={"DD/MM/YYYY"} />
          </Form.Item>
          <Form.Item
            name={"dateTo"}
            label={formatMessage({ id: "date.end" })}
            tooltip={formatMessage({ id: "form.help.end.date.item" }, { item: formatMessage({ id: "uniform" }) })}
            style={formItemSharedStyles}
          >
            <DatePicker size={"middle"} format={"DD/MM/YYYY"} />
          </Form.Item>
          {Object.entries(transformUniformItemsResponse(uniforms.items)).map((entry) => {
            const itemType = entry[0] as UniformItemTypes;
            const itemData = entry[1];

            const isBottom = itemType === UniformItemTypes.Bottom;
            const bottomIntlKey = bottomType === UniformItemBottomTypes.Skirts ? "uniform.skirts" : "uniform.shorts";
            const bottomTooltipIntlKey = bottomType === UniformItemBottomTypes.Skirts ? "uniform.help.skirts" : "uniform.help.shorts";

            return (
              <Form.Item
                key={itemType}
                style={formItemSharedStyles}
                label={formatMessage({ id: isBottom ? bottomIntlKey : `uniform.${itemType}` })}
                tooltip={formatMessage({ id: isBottom ? bottomTooltipIntlKey : `uniform.help.${itemType}` })}
              >
                <Flex>
                  {Object.keys(itemData.colors).map((color) => (
                    <Fragment key={color}>
                      <Form.Item<FormType>
                        key={color}
                        style={formItemSharedStyles}
                        name={["uniformParts", itemType, "colors", color as UniformItemColors]}
                      >
                        <ColorPicker defaultFormat="hex" disabledAlpha />
                      </Form.Item>
                      <FormInput<FormType> hidden name={["uniformParts", itemType, "uniformItemId"]} />
                      <FormInput<FormType> hidden name={["uniformParts", itemType, "nameLocal"]} />
                      <FormInput<FormType> hidden name={["uniformParts", itemType, "nameLatin"]} />
                    </Fragment>
                  ))}
                </Flex>
              </Form.Item>
            );
          })}
          <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ textAlign: "right" }}>
            <Space>
              <PermissionCheck
                permissionKeys={[PermissionNames.UNIFORM]}
                permissionOptions={[isEdit ? PermissionOperations.UPDATE : PermissionOperations.CREATE]}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isUpsertUniformInProgress}
                  onClick={onSubmitButtonHandler}
                  disabled={isMutationDisabled}
                >
                  {formatMessage({ id: isEdit ? "update" : "add" })}
                </Button>
              </PermissionCheck>
              {properties.uniform && (
                <PermissionCheck permissionKeys={[PermissionNames.UNIFORM]} permissionOptions={[PermissionOperations.DELETE]}>
                  <Button
                    type="default"
                    loading={isDeletingUniformInProgress || isDeletingUniformItemInProgress}
                    onClick={onDeleteButtonHandler}
                    disabled={isMutationDisabled}
                  >
                    {formatMessage({ id: "delete" })}
                  </Button>
                </PermissionCheck>
              )}
            </Space>
          </Form.Item>
        </Form>
      </div>
      <Flex>
        {uniforms.items.map((uniformConfig) => (
          <UniformSet uniformConfig={uniformConfig} uniformParts={uniformParts} bottomType={bottomType} key={uniformConfig.titleIntlKey} />
        ))}
      </Flex>
    </StyledContainer>
  );
};
